// RichContentView — vẽ HTML do RichDescriptionEditor (TipTap) soạn ra, CHỈ ĐỌC.
//
// Vì sao không `dangerouslySetInnerHTML`: nội dung soạn ở MỘT nơi (vd bài «Tin mới» ở Core) rồi
// hiện cho người ở nơi khác (khách của CNV Work). HTML thô đi thẳng vào DOM là cửa XSS lưu trữ —
// một thẻ `<img onerror=…>` là chạy mã trên máy mọi khách. Ở đây HTML được PARSE lại bằng chính
// lược đồ ProseMirror của TipTap: chỉ nút/thuộc tính mà lược đồ khai mới sống sót —
// `<script>`, `<iframe>`, `style`, mọi `on*=` rơi rụng; link `javascript:` bị extension Link
// chặn cả lúc parse lẫn lúc vẽ (`isAllowedUri`); ảnh `data:` bị Image bỏ (`allowBase64: false`).
// Máy chủ cũng lọc một lượt lúc lưu, nhưng lớp này mới là lớp quyết định.
//
// Bấm link: đi qua kernel `openEntityLink` (nội bộ ⇒ popup/SPA, ngoài ⇒ tab mới) — `onNavigate`
// để bên bọc (vd khung đọc bài dạng popup) tự đóng trước khi trang đổi.
// Bấm ảnh: phóng to tại chỗ.
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { X } from 'lucide-react';
import { openEntityLink, toInternalPath } from '../../utils/openEntity';

import { useLingui } from '@lingui/react/macro';

// StarterKit v3 ĐÃ gồm Link (+ Underline) — cấu hình qua khoá `link`, đừng thêm Link lần hai
// (trùng tên extension ⇒ TipTap cảnh báo, và bản cấu hình nào thắng là tuỳ thứ tự).
const EXTENSIONS = [
  StarterKit.configure({
    heading: { levels: [2, 3] },
    link: {
      openOnClick: false,
      autolink: false,
      HTMLAttributes: { class: 'text-blue-600 underline', rel: 'noopener noreferrer nofollow', target: '_blank' },
    },
  }),
  Image.configure({ HTMLAttributes: { class: 'rounded-lg max-w-full h-auto cursor-zoom-in' } }),
];

export default function RichContentView({ html, className = '', onNavigate }) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const [zoom, setZoom] = useState(null);

  const editor = useEditor({
    editable: false,
    extensions: EXTENSIONS,
    content: html || '',
    editorProps: {
      attributes: { class: `prose prose-sm sm:prose-base max-w-none outline-none text-gray-800 ${className}` },
    },
  }, [html]);

  const onClick = (e) => {
    const img = e.target?.closest?.('img');
    if (img?.src) { e.preventDefault(); setZoom(img.src); return; }
    const a = e.target?.closest?.('a[href]');
    if (!a) return;
    e.preventDefault();
    const href = a.getAttribute('href');
    if (!href) return;
    // Link nội bộ: đóng lớp phủ của bên bọc TRƯỚC, rồi mới điều hướng — không thì trang đổi
    // phía sau một khung đọc bài vẫn che kín màn hình.
    if (toInternalPath(href) && typeof onNavigate === 'function') onNavigate();
    openEntityLink(href, { navigate });
  };

  if (!editor) return null;

  return (
    <>
      <div onClick={onClick}>
        <EditorContent editor={editor} />
      </div>
      {zoom && createPortal(
        <div className="fixed inset-0 z-[10020] bg-black/80 flex items-center justify-center p-4" onClick={() => setZoom(null)}>
          <button type="button" onClick={() => setZoom(null)} aria-label={t`Đóng ảnh`}
                  className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center">
            <X size={18} />
          </button>
          <img src={zoom} alt="" className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" />
        </div>,
        document.body,
      )}
    </>
  );
}
